import { ExecutiveKPIs, ManagerialAlert } from '../../../../hooks/useExecutiveData';

interface Props {
  kpis: ExecutiveKPIs;
  alerts: ManagerialAlert[];
}

const KPICard = ({
  icon, label, value, sub, color,
}: { icon: string; label: string; value: string | number; sub?: string; color: string }) => (
  <div className="bg-white/5 border border-white/10 rounded-2xl p-5 flex flex-col gap-3 hover:bg-white/[0.07] transition-colors">
    <div className="flex items-center justify-between">
      <span className="text-[12px] text-white/50">{label}</span>
      <div className={`w-9 h-9 flex items-center justify-center rounded-xl ${color}`}>
        <i className={`${icon} text-[17px] text-white`} />
      </div>
    </div>
    <div>
      <p className="text-[26px] font-700 text-white leading-tight">{value}</p>
      {sub && <p className="text-[12px] text-white/40 mt-0.5">{sub}</p>}
    </div>
  </div>
);

const alertColors = {
  danger:  { bg: 'bg-red-500/10',    border: 'border-red-500/30',    text: 'text-red-400'    },
  warning: { bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', text: 'text-yellow-400' },
  info:    { bg: 'bg-violet-500/10', border: 'border-violet-500/30', text: 'text-violet-400' },
  success: { bg: 'bg-emerald-500/10',border: 'border-emerald-500/30',text: 'text-emerald-400'},
};

export const ExecutiveOverview = ({ kpis, alerts }: Props) => {
  const pctActive = kpis.totalUsers > 0
    ? Math.round((kpis.activeUsers / kpis.totalUsers) * 100)
    : 0;
  const pctWithTx = kpis.totalUsers > 0
    ? Math.round((kpis.usersWithTransactions / kpis.totalUsers) * 100)
    : 0;

  return (
    <div>
      {/* KPI Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        <KPICard icon="ri-team-line"           label="Usuários cadastrados" value={kpis.totalUsers}              sub="base total"                  color="bg-violet-600"  />
        <KPICard icon="ri-user-follow-line"    label="Usuários ativos"      value={kpis.activeUsers}             sub={`${pctActive}% da base`}     color="bg-emerald-600" />
        <KPICard icon="ri-exchange-line"       label="Com movimentação"     value={kpis.usersWithTransactions}   sub={`${pctWithTx}% da base`}     color="bg-cyan-600"    />
        <KPICard icon="ri-bank-line"           label="Contas cadastradas"   value={kpis.totalAccounts}           sub="total da base"               color="bg-indigo-600"  />
        <KPICard icon="ri-bank-card-line"      label="Cartões cadastrados"  value={kpis.totalCards}              sub="total da base"               color="bg-pink-600"    />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
        <KPICard icon="ri-file-list-3-line"    label="Lançamentos"          value={kpis.totalTransactions.toLocaleString('pt-BR')} sub="no período"           color="bg-amber-600"   />
        <KPICard icon="ri-flag-line"           label="Metas ativas"         value={kpis.totalActiveGoals}        sub="em andamento"                color="bg-teal-600"    />
        <KPICard icon="ri-calendar-check-line" label="Calendário ativo"     value={kpis.usersWithCalendar}       sub="usuários com eventos"        color="bg-orange-600"  />
        <KPICard icon="ri-notification-3-line" label="Notificações pendentes" value={kpis.usersWithPendingNotifications} sub="usuários com não lidas" color="bg-rose-600"    />
      </div>

      {/* Alertas Gerenciais */}
      <h2 className="text-[16px] font-600 text-white mb-4 flex items-center gap-2">
        <div className="w-5 h-5 flex items-center justify-center">
          <i className="ri-radar-line text-violet-400 text-[16px]" />
        </div>
        Alertas Gerenciais
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {alerts.map((alert, idx) => {
          const cls = alertColors[alert.type];
          return (
            <div
              key={idx}
              className={`flex items-start gap-3 px-4 py-3.5 rounded-xl border ${cls.bg} ${cls.border}`}
            >
              <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${cls.bg} shrink-0 mt-0.5`}>
                <i className={`${alert.icon} text-[16px] ${cls.text}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className={`text-[13px] font-600 ${cls.text}`}>{alert.title}</p>
                  {alert.value && (
                    <span className={`text-[11px] font-700 px-2 py-0.5 rounded-full ${cls.bg} ${cls.text} border ${cls.border}`}>
                      {alert.value}
                    </span>
                  )}
                </div>
                <p className="text-[12px] text-white/40 mt-0.5 leading-relaxed">{alert.description}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
